import React, { useState } from "react";
import Lottie from "lottie-react";
import growth from "../../assets/calculator/growth.json";
import tw from "twin.macro";
import repayment from "../../assets/calculator/repayment.jpg";
import borrow from "../../assets/calculator/borrowing.jpg";
import rates from "../../assets/calculator/rates.jpg";
import { calculateAccLevy } from "./taxConfig";

const Main = tw.div`relative`;
const Container = tw.div`mx-auto max-w-2xl py-24 px-4 sm:py-32 sm:px-6 lg:max-w-7xl lg:px-8`;
const HeadingSection1 = tw.div`max-w-3xl`;
const HeadingSection2 = tw.div`max-w-3xl mt-12`;
const Heading = tw.h1`flex flex-col text-3xl font-bold text-gray-800 mb-2`;
const Heading2 = tw.h2`flex flex-col text-3xl font-bold text-gray-800 mb-2`;
const HeadingDescription1 = tw.p`flex flex-col text-base font-light text-gray-500 mt-3`;
const HeadingDescription2 = tw.p`flex flex-col text-xs font-light text-gray-500 italic mt-3`;
const TwoColumn = tw.div`grid grid-cols-1 gap-y-20 gap-x-8 lg:grid-cols-3`;
const TextColumn = tw.div`lg:col-span-2`;
const ImageColumn = tw.div`lg:col-span-1 h-full w-full object-cover object-center`;
const CalculatorSection = tw.div`mx-auto flex flex-col md:flex-row bg-gray-100 mt-8 shadow-xl rounded-2xl`;
const Calculator = tw.div`cursor-pointer flex flex-col border-r border-gray-200 md:w-2/5`;
const CalculatorRow1 = tw.div`relative mt-10 ml-10 mr-10 grid grid-cols-2 gap-2`;
const CalculatorRow2 = tw.div`relative mt-5 ml-10 mr-10 grid grid-cols-2 gap-2`;
const Div = tw.div``;
const Label = tw.label`text-base text-gray-500`;
const Select = tw.select`w-full rounded-md bg-white rounded border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-base outline-none text-gray-700 h-12 px-3`;
const Input = tw.input`w-full rounded-md bg-white rounded border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 h-12 text-base outline-none text-gray-700 py-1 px-3 leading-8 transition-colors duration-200 ease-in-out`;
const Button = tw.button`m-10 text-white bg-[#ed1c24] border-0 py-2 px-6  focus:outline-none rounded-lg text-base transform active:scale-x-90 active:scale-y-90 transition-transform`;
const ValueSection = tw.div`bg-white rounded-r-2xl md:w-3/5`;
const ValueHeading = tw.div`flex border-b justify-around bg-gray-100 rounded-tr-2xl text-gray-500 h-14 text-center`;
const ValueContent = tw.div`flex flex-col justify-center m-5`;
const ValueRow = tw.div`mt-3 mb-12 text-center text-4xl text-gray-800`;
const Value = tw.div`text-center text-8xl text-gray-800 decoration-2`;
const ValueDescription = tw.p`text-xs text-left font-light italic text-gray-500 mt-12 m-5`;
const MoreCalculators = tw.div`mt-6 grid grid-cols-1 gap-y-10 gap-x-6 sm:grid-cols-2 lg:grid-cols-3 xl:gap-x-8`;
const MoreCalculatorsContainer = tw.div`aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-2xl shadow-xl bg-gray-100 group-hover:opacity-75 lg:aspect-none lg:h-80`;
const MoreCalculatorsImage = tw.img`object-cover object-center lg:h-full lg:w-full`;
const H3 = tw.h3`mt-4 text-base text-gray-700 text-center`;
const PayeCalculator = () => {
  const [grossIncome, setGrossIncome] = useState();
  const [grossIncomePeriod, setGrossIncomePeriod] = useState("week");
  const [kiwiSaver, setKiwiSaver] = useState(0);
  const [studentLoan, setStudentLoan] = useState("0");
  const [netAnnualPay, setAnnualNetPay] = useState(0);
  const [netMonthlyPay, setMonthlyNetPay] = useState(0);
  const [netFortnightlyPay, setFortnightlyNetPay] = useState(0);
  const [netWeeklyPay, setWeeklyNetPay] = useState(0);
  const calculateNetPay = (e) => {
    e.preventDefault();
    let taxableIncome = Number(grossIncome);

    if (grossIncomePeriod === "week") {
      taxableIncome = taxableIncome * 52;
    } else if (grossIncomePeriod === "fortnight") {
      taxableIncome = taxableIncome * 26;
    } else if (grossIncomePeriod === "month") {
      taxableIncome = taxableIncome * 12;
    }

    let taxAmount;

    if (taxableIncome <= 15600) {
      taxAmount = taxableIncome * 0.105;
    } else if (taxableIncome <= 53500) {
      taxAmount = 15600 * 0.105 + (taxableIncome - 15600) * 0.175;
    } else if (taxableIncome <= 78100) {
      taxAmount =
        15600 * 0.105 + (53500 - 15600) * 0.175 + (taxableIncome - 53500) * 0.3;
    } else if (taxableIncome <= 180000) {
      taxAmount =
        15600 * 0.105 +
        (53500 - 15600) * 0.175 +
        (78100 - 53500) * 0.3 +
        (taxableIncome - 78100) * 0.33;
    } else {
      taxAmount =
        15600 * 0.105 +
        (53500 - 15600) * 0.175 +
        (78100 - 53500) * 0.3 +
        (180000 - 78100) * 0.33 +
        (taxableIncome - 180000) * 0.39;
    }

    const accAmount = calculateAccLevy(taxableIncome);
    const STUDENT_LOAN_THRESHOLD = 21268;

    const kiwiSaverAmount = (taxableIncome * kiwiSaver) / 100;
    const studentLoanAmount =
      taxableIncome > STUDENT_LOAN_THRESHOLD
        ? (taxableIncome - STUDENT_LOAN_THRESHOLD) * 0.12 * studentLoan
        : 0;
    setAnnualNetPay(
      taxableIncome -
        taxAmount -
        kiwiSaverAmount -
        accAmount -
        studentLoanAmount,
    );
    setMonthlyNetPay(
      (
        (taxableIncome -
          taxAmount -
          kiwiSaverAmount -
          accAmount -
          studentLoanAmount) /
        12
      ).toFixed(2),
    );
    setFortnightlyNetPay(
      (
        (taxableIncome -
          taxAmount -
          kiwiSaverAmount -
          accAmount -
          studentLoanAmount) /
        26
      ).toFixed(2),
    );

    setWeeklyNetPay(
      (
        (taxableIncome -
          taxAmount -
          kiwiSaverAmount -
          accAmount -
          studentLoanAmount) /
        52
      ).toFixed(2),
    );
  };

  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const data = [
    {
      label: "Annual",
      value: "Annual",
      desc: `$${netAnnualPay}`,
    },
    {
      label: "Monthly",
      value: "Monthly",
      desc: `$${netMonthlyPay}`,
    },
    {
      label: "Fortnightly",
      value: "Fortnightly",
      desc: `$${netFortnightlyPay}`,
    },
    {
      label: "Weekly",
      value: "Weekly",
      desc: `$${netWeeklyPay}`,
    },
  ];

  const calculators = [
    {
      id: 1,
      name: "Repayment Calculator",
      href: "/repayment-calculator",
      imageSrc: repayment,
      imageAlt: "repayment calculator, auckland mortgage broker",
    },
    {
      id: 2,
      name: "Borrowing Calculator",
      href: "/borrowing-calculator",
      imageSrc: borrow,
      imageAlt:
        "borrowing calculator, how much can I borrow? auckland mortgage broker",
    },
    {
      id: 3,
      name: "Compare Rates",
      href: "/rates-compare",
      imageSrc: rates,
      imageAlt:
        "compare rates, compare interest rates, auckland mortgage broker",
    },
  ];

  return (
    <Main>
      <head>
        <meta
          name="description"
          content="Use our PAYE calculator to work out salary and wage deductions and estimate how much you could take home after paying tax."
        />
        <meta
          property="og:site_name"
          content="PAYE Calculator - New Zealand Take Home Pay"
        />
        <meta
          property="og:title"
          content="PAYE Calculator - New Zealand Take Home Pay"
        />
        <meta
          property="og:description"
          content="Use our PAYE calculator to work out salary and wage deductions and estimate how much you could take home after paying tax."
        />
        <meta property="og:locale" content="en-NZ" />
        <meta
          name="twitter:title"
          content="PAYE Calculator - New Zealand Take Home Pay"
        />
        <meta
          name="twitter:description"
          content="Use our PAYE calculator to work out salary and wage deductions and estimate how much you could take home after paying tax."
        />
        <title>PAYE Calculator - New Zealand Take Home Pay</title>
        <link rel="canonical" href="https://bicare.co.nz/paye-calculator" />
      </head>
      <Container>
        <HeadingSection1>
          <Heading>PAYE Calculator - New Zealand Take Home Pay</Heading>
          <HeadingDescription1>
            Use our PAYE calculator to work out salary and wage deductions and
            estimate how much you could take home after paying tax.
          </HeadingDescription1>
          <HeadingDescription2>
            This calculator includes the 2026/27 ACC earners&apos; levy of 1.75%
            on income up to $156,641, and the 39% personal tax rate on income
            over $180,000.
          </HeadingDescription2>
        </HeadingSection1>
        <TwoColumn>
          <TextColumn>
            <CalculatorSection>
              <Calculator>
                <CalculatorRow1>
                  {/*gross*/}
                  <Div>
                    <Label>Gross Income:</Label>
                    <Input
                      type="number"
                      value={grossIncome}
                      onChange={(e) => setGrossIncome(e.target.value)}
                      min="0"
                      placeholder="$"
                    ></Input>
                  </Div>
                  {/*period*/}
                  <Div>
                    <Label>Period:</Label>
                    <Select
                      value={grossIncomePeriod}
                      onChange={(e) => setGrossIncomePeriod(e.target.value)}
                    >
                      <option value="week">Week</option>
                      <option value="fortnight">Fortnight</option>
                      <option value="month">Month</option>
                      <option value="year">Year</option>
                    </Select>
                  </Div>
                </CalculatorRow1>
                <CalculatorRow2>
                  {/*KiwiSaver*/}
                  <Div>
                    <Label>KiwiSaver:</Label>
                    <Select
                      value={kiwiSaver}
                      onChange={(e) => setKiwiSaver(e.target.value)}
                    >
                      <option value={0}>0%</option>
                      <option value={2}>2%</option>
                      <option value={3}>3%</option>
                      <option value={4}>4%</option>
                      <option value={6}>6%</option>
                      <option value={8}>8%</option>
                      <option value={10}>10%</option>
                    </Select>
                  </Div>
                  {/*studentLoan*/}
                  <Div>
                    <Label>Student Loan:</Label>
                    <Select
                      value={studentLoan}
                      onChange={(e) => setStudentLoan(e.target.value)}
                    >
                      <option value={0}>No</option>
                      <option value={1}>Yes</option>
                    </Select>
                  </Div>
                </CalculatorRow2>
                <Button onClick={calculateNetPay}>Calculate</Button>
              </Calculator>
              <ValueSection>
                <ValueHeading>
                  {data.map((label, idx) => {
                    return (
                      <div
                        key={idx}
                        className={`py-4 border-b-4 transition-colors duration-300 ${
                          idx === activeTabIndex
                            ? "border-[#ed1c24] border-b-2"
                            : "border-transparent hover:border-gray-400 hover:scale-105"
                        }`}
                        // Change the active tab on click.
                        onClick={() => setActiveTabIndex(idx)}
                      >
                        {label.label}
                      </div>
                    );
                  })}
                </ValueHeading>
                <ValueContent>
                  <ValueRow>Your take home pay is:</ValueRow>
                  <Value>{data[activeTabIndex].desc}</Value>
                </ValueContent>
                <ValueDescription>
                  This calculator is for information purposes only and does not
                  provide financial advice.
                </ValueDescription>
              </ValueSection>
            </CalculatorSection>
          </TextColumn>
          <ImageColumn>
            <Lottie
              animationData={growth}
              loop={true}
              height={500}
              mwidth={500}
            />
          </ImageColumn>
        </TwoColumn>
        <HeadingSection2>
          <Heading2>More helpful calculators.</Heading2>
          <HeadingDescription1>
            Whether you need a mortgage calculator to work out your borrowing
            power, mortgage repayments or after tax income, we have got your
            covered.
          </HeadingDescription1>
        </HeadingSection2>
        <MoreCalculators>
          {calculators.map((calculator) => (
            <a
              key={calculator.id}
              href={calculator.href}
              className="group relative"
            >
              <MoreCalculatorsContainer>
                <MoreCalculatorsImage
                  src={calculator.imageSrc}
                  alt={calculator.imageAlt}
                  title={calculator.name}
                  loading="lazy"
                ></MoreCalculatorsImage>
              </MoreCalculatorsContainer>
              <H3>{calculator.name}</H3>
            </a>
          ))}
        </MoreCalculators>
      </Container>
    </Main>
  );
};

export default PayeCalculator;
